// Pure command model for the dev console — no DOM, no supabase. The SQL RPCs
// re-validate and re-enforce everything here; this layer exists for instant
// feedback and for filtering the help list to the caller's role.
import type { Role } from './roles';
import { SHOP_ORDER } from '../config/unlocks';
import { HUB_LEVELS } from '../config/hubLevels';
import { POTION_COMMAND_NAMES, potionFromCommandName } from '../config/potions';
import { isFeatureFlag, FEATURE_FLAGS } from '../config/featureFlags';
import { ACHIEVEMENTS, isAchievementId } from '../config/achievements';
import { isShopAvatarCode, SHOP_AVATAR_CODES } from '../config/avatars';
import { GIFT_ITEMS, isGiftItem } from '../config/giftItems';
import { HAMBI_LABELS, normalizeCode, playsForStars } from '../config/creator';

export type GiveWhat =
  | 'coins'
  | 'tower'
  | 'unlock'
  | 'level'
  | 'bags'
  | 'rank'
  | 'achieve'
  | 'hublevel'
  | 'debris'
  | 'moonstone'
  | 'capycoins'
  | 'capsules'
  | 'goldenbags'
  | 'darkcoins'
  | 'lightcoins'
  | 'potion'
  | 'icon'
  | 'item';

/**
 * What `/remove` can take (spec 2026-10-06). `clear` empties the Storage shelf;
 * the amount kinds subtract (never below 0); tower/item/potion take a name.
 */
export const REMOVE_KINDS = [
  'coins',
  'darkcoins',
  'lightcoins',
  'bags',
  'goldenbags',
  'capsules',
  'tower',
  'item',
  'potion',
  'clear',
] as const;
export type RemoveWhat = (typeof REMOVE_KINDS)[number];

/** The largest amount one `/remove` may name — the coin ceiling a save can hold. */
export const REMOVE_AMOUNT_MAX = 2_000_000_000;
/** The largest count of one item or potion one `/remove` may name. */
export const REMOVE_COUNT_MAX = 1_000;

export type ParsedCommand =
  | { cmd: 'give'; target: string; what: GiveWhat; value: string }
  /** `value` is the tower/item/potion name ('' otherwise); `amount` how many. */
  | { cmd: 'remove'; target: string; what: RemoveWhat; value: string; amount: number }
  | { cmd: 'message'; target: string; text: string }
  | { cmd: 'ban'; target: string }
  | { cmd: 'op'; target: string }
  | { cmd: 'skip'; target: string }
  | { cmd: 'restock'; what: 'potionshop' }
  /**
   * Explore (spec 2026-11-02 §4.5, 2026-11-03 §4): `value` null = none (hambi)
   * / auto (star); `plays` sets the play count. A level or a chapter code.
   */
  | { cmd: 'rate'; what: 'hambi' | 'star' | 'plays'; code: string; value: number | null }
  | { cmd: 'server'; ban: boolean; code: string }
  | {
      cmd: 'suggest';
      code: string;
      kind: 'star' | 'hambi' | 'ban' | 'unban';
      value: number | null;
    };

export type ParseResult = { ok: true; command: ParsedCommand } | { ok: false; error: string };

/** The most coins one `/give` may carry from an admin (spec 2026-10-06). */
export const ADMIN_COIN_GIVE_MAX = 1_000_000_000;
/** … and from a lower or higher dev, unchanged. */
export const DEV_COIN_GIVE_MAX = 1_000_000;

const BOUNDS: Record<
  | 'coins'
  | 'level'
  | 'bags'
  | 'rank'
  | 'hublevel'
  | 'debris'
  | 'moonstone'
  | 'capycoins'
  | 'capsules'
  | 'goldenbags'
  | 'darkcoins'
  | 'lightcoins',
  [number, number]
> = {
  // The parse bound is the ADMIN's (spec 2026-10-06); lower and higher devs
  // are held to DEV_COIN_GIVE_MAX by commandRefusal, and dev_give again.
  coins: [1, ADMIN_COIN_GIVE_MAX],
  capycoins: [1, 1_000_000],
  // Dark Hambi Coins (spec 2026-10-06): the coins' bound.
  darkcoins: [1, 1_000_000],
  // Light Hambi Coins (spec 2026-10-08): the same bound.
  lightcoins: [1, 1_000_000],
  // Capsules go on the Storage shelf, so they take the bags' bound.
  capsules: [1, 1_000],
  // Golden Takeout Bags on the Storage shelf: the bags' bound too.
  goldenbags: [1, 1_000],
  level: [2, 1_000],
  bags: [1, 1_000],
  rank: [1, 40],
  // The Hub's levels won so far (1 = Hambi Bar, 2 = HTD Exhibition).
  hublevel: [1, HUB_LEVELS.length],
  debris: [1, 100],
  // One Moon Stone exists, ever — so the only meaningful amount is one, and
  // the bound says so rather than silently ignoring a bigger number.
  moonstone: [1, 1],
};

export function parseCommand(input: string): ParseResult {
  const trimmed = input.trim();
  if (!trimmed.startsWith('/')) return { ok: false, error: 'Commands start with /' };
  const [head, ...rest] = trimmed.slice(1).split(/\s+/);

  if (head === 'give') {
    const [target, rawWhat, ...valueParts] = rest;
    // The user-facing spelling is "achieve"; the common misspelling parses too.
    const what = rawWhat === 'acheive' ? 'achieve' : rawWhat;
    const value = valueParts.join(' ');
    if (!target || !what || !value) {
      return {
        ok: false,
        error:
          'Usage: /give <I|username|global> <coins|tower|unlock|level|bags|rank|achieve|hublevel|potion|debris|moonstone|capycoins|capsules|goldenbags|darkcoins|lightcoins|icon|item> <value>',
      };
    }
    if (
      what === 'coins' ||
      what === 'level' ||
      what === 'bags' ||
      what === 'rank' ||
      what === 'hublevel' ||
      what === 'debris' ||
      what === 'moonstone' ||
      what === 'capycoins' ||
      what === 'capsules' ||
      what === 'goldenbags' ||
      what === 'darkcoins' ||
      what === 'lightcoins'
    ) {
      if (!/^[0-9]+$/.test(value)) return { ok: false, error: `${what} needs a whole number` };
      const n = parseInt(value, 10);
      const [lo, hi] = BOUNDS[what];
      if (n < lo || n > hi) return { ok: false, error: `${what} must be ${lo}–${hi}` };
      return { ok: true, command: { cmd: 'give', target, what, value: String(n) } };
    }
    if (what === 'tower') {
      if (!(SHOP_ORDER as readonly string[]).includes(value)) {
        return { ok: false, error: `Unknown tower "${value}". Towers: ${SHOP_ORDER.join(', ')}` };
      }
      return { ok: true, command: { cmd: 'give', target, what, value } };
    }
    if (what === 'icon') {
      // An Avatar Shop icon, by its code (spec 2026-09-21 §6). Same list the
      // database validates against, so a gift the client accepts is never one
      // set_avatar would later refuse.
      const code = value.toLowerCase();
      if (!isShopAvatarCode(code)) {
        return {
          ok: false,
          error: `Unknown icon "${value}". Icons: ${SHOP_AVATAR_CODES.join(', ')}`,
        };
      }
      return { ok: true, command: { cmd: 'give', target, what, value: code } };
    }
    if (what === 'item') {
      // Any Storage item that is not a bag or a potion (spec 2026-09-22).
      const name = value.toLowerCase();
      if (!isGiftItem(name)) {
        return { ok: false, error: `Unknown item "${value}". Items: ${GIFT_ITEMS.join(', ')}` };
      }
      return { ok: true, command: { cmd: 'give', target, what, value: name } };
    }
    if (what === 'potion') {
      // The command name is the recipient-facing spelling ("bottleburger").
      if (!potionFromCommandName(value)) {
        return {
          ok: false,
          error: `Unknown potion "${value}". Potions: ${Object.keys(POTION_COMMAND_NAMES).join(', ')}`,
        };
      }
      return { ok: true, command: { cmd: 'give', target, what, value: value.toLowerCase() } };
    }
    if (what === 'unlock') {
      if (!isFeatureFlag(value)) {
        return {
          ok: false,
          error: `Unknown unlock "${value}". Unlocks: ${FEATURE_FLAGS.join(', ')}`,
        };
      }
      return { ok: true, command: { cmd: 'give', target, what, value } };
    }
    if (what === 'achieve') {
      // Ids may arrive quoted (the help example writes "stageness").
      const quoted = /^"([\s\S]*)"$/.exec(value);
      const id = (quoted ? quoted[1] : value).trim();
      // Pseudo-values: clear the recipient's list / award everything.
      if (id === 'reset' || id === 'all') {
        return { ok: true, command: { cmd: 'give', target, what, value: id } };
      }
      if (!isAchievementId(id)) {
        return {
          ok: false,
          error: `Unknown achievement "${id}". Achievements: ${ACHIEVEMENTS.map((a) => a.id).join(', ')}`,
        };
      }
      return { ok: true, command: { cmd: 'give', target, what, value: id } };
    }
    return { ok: false, error: `Unknown gift kind "${what}"` };
  }

  if (head === 'remove') return parseRemove(rest);

  if (head === 'message') {
    const [target, ...textParts] = rest;
    let text = textParts.join(' ').trim();
    const quoted = /^"([\s\S]*)"$/.exec(text);
    if (quoted) text = quoted[1].trim();
    if (!target || !text) return { ok: false, error: 'Usage: /message <username|global> "text"' };
    if (text.length > 200) return { ok: false, error: 'Message too long (max 200 characters)' };
    return { ok: true, command: { cmd: 'message', target, text } };
  }

  if (head === 'ban') {
    const [target, ...extra] = rest;
    if (!target || extra.length > 0 || target === 'I' || target === 'global') {
      return { ok: false, error: 'Usage: /ban <username>' };
    }
    return { ok: true, command: { cmd: 'ban', target } };
  }

  if (head === 'restock') {
    const [what, ...extra] = rest;
    if (what !== 'potionshop' || extra.length > 0) {
      return { ok: false, error: 'Usage: /restock potionshop' };
    }
    return { ok: true, command: { cmd: 'restock', what: 'potionshop' } };
  }

  if (head === 'op') {
    const [target, ...extra] = rest;
    if (!target || extra.length > 0 || target === 'global') {
      return { ok: false, error: 'Usage: /op <I|username>' };
    }
    return { ok: true, command: { cmd: 'op', target } };
  }

  if (head === 'skip') {
    const [target, ...extra] = rest;
    if (!target || extra.length > 0 || target === 'global') {
      return { ok: false, error: 'Usage: /skip <I|username>' };
    }
    return { ok: true, command: { cmd: 'skip', target } };
  }

  if (head === 'rate') return parseRate(rest);
  if (head === 'server') return parseServer(rest);
  if (head === 'suggest') return parseSuggest(rest);

  return { ok: false, error: `Unknown command "/${head ?? ''}"` };
}

const RATE_USAGE =
  'Usage: /rate hambi <code> <1-5|none> · /rate star <code> <0-5|auto> · /rate plays <code> <number>';
/** The most plays `/rate plays` may set — mirrored by admin_rate_level. */
export const MAX_RATE_PLAYS = 1_000_000;
const SERVER_USAGE = 'Usage: /server ban <code> · /server unban <code>';
const SUGGEST_USAGE = 'Usage: /suggest <code> <0-5> [stars|hambis] · /suggest <code> <ban|unban>';
const BAD_CODE = 'A level code is four letters/numbers, like K7Q2 (shown as #K7Q2 in Explore)';

/** `/rate hambi|star|plays <code> <n>` (admin). */
function parseRate(rest: string[]): ParseResult {
  const [rawWhat, rawCode, rawValue, ...extra] = rest;
  const w = rawWhat?.toLowerCase();
  const what =
    w === 'hambis' ? 'hambi' : w === 'stars' ? 'star' : w === 'play' ? 'plays' : (w ?? '');
  if (
    (what !== 'hambi' && what !== 'star' && what !== 'plays') ||
    !rawCode ||
    !rawValue ||
    extra.length > 0
  ) {
    return { ok: false, error: RATE_USAGE };
  }
  const code = normalizeCode(rawCode);
  if (!code) return { ok: false, error: BAD_CODE };
  const v = rawValue.toLowerCase().replace(/,/g, '');
  if (what === 'plays') {
    if (!/^[0-9]{1,7}$/.test(v)) return { ok: false, error: RATE_USAGE };
    const n = parseInt(v, 10);
    if (n > MAX_RATE_PLAYS) return { ok: false, error: 'Plays must be 0–1,000,000' };
    return rateOk('plays', code, n);
  }
  if (what === 'hambi' && v === 'none') return rateOk(what, code, null);
  if (what === 'star' && v === 'auto') return rateOk(what, code, null);
  if (!/^[0-9]$/.test(v)) return { ok: false, error: RATE_USAGE };
  const n = parseInt(v, 10);
  if (what === 'hambi' && (n < 1 || n > 5)) return { ok: false, error: 'Hambis must be 1–5' };
  if (what === 'star' && n > 5) return { ok: false, error: 'Stars must be 0–5' };
  return rateOk(what, code, n);
}

function rateOk(what: 'hambi' | 'star' | 'plays', code: string, value: number | null): ParseResult {
  return { ok: true, command: { cmd: 'rate', what, code, value } };
}

/** `/server ban|unban <code>` (admin). */
function parseServer(rest: string[]): ParseResult {
  const [verb, rawCode, ...extra] = rest;
  if ((verb !== 'ban' && verb !== 'unban') || !rawCode || extra.length > 0) {
    return { ok: false, error: SERVER_USAGE };
  }
  const code = normalizeCode(rawCode);
  if (!code) return { ok: false, error: BAD_CODE };
  return { ok: true, command: { cmd: 'server', ban: verb === 'ban', code } };
}

/** `/suggest <code> <n> [stars|hambis]` · `/suggest <code> ban|unban` (devs). */
function parseSuggest(rest: string[]): ParseResult {
  const [rawCode, rawWhat, rawUnit, ...extra] = rest;
  if (!rawCode || !rawWhat || extra.length > 0) return { ok: false, error: SUGGEST_USAGE };
  const code = normalizeCode(rawCode);
  if (!code) return { ok: false, error: BAD_CODE };
  const w = rawWhat.toLowerCase();
  if (w === 'ban' || w === 'unban') {
    if (rawUnit) return { ok: false, error: SUGGEST_USAGE };
    return { ok: true, command: { cmd: 'suggest', code, kind: w, value: null } };
  }
  if (!/^[0-9]$/.test(w)) return { ok: false, error: SUGGEST_USAGE };
  const n = parseInt(w, 10);
  const unit = (rawUnit ?? 'stars').toLowerCase();
  // "number of stars" is the default; `hambis` asks for a difficulty.
  if (unit === 'stars' || unit === 'star') {
    if (n > 5) return { ok: false, error: 'Stars must be 0–5' };
    return { ok: true, command: { cmd: 'suggest', code, kind: 'star', value: n } };
  }
  if (unit === 'hambis' || unit === 'hambi') {
    if (n < 1 || n > 5) return { ok: false, error: 'Hambis must be 1–5' };
    return { ok: true, command: { cmd: 'suggest', code, kind: 'hambi', value: n } };
  }
  return { ok: false, error: SUGGEST_USAGE };
}

/** "3 Hambis (Mild)" / "4 stars" / "250 plays" — the console's and the inbox's wording. */
export function ratingWords(kind: 'hambi' | 'star' | 'plays', value: number | null): string {
  if (kind === 'plays') {
    const n = value ?? 0;
    return `${n.toLocaleString('en-US')} ${n === 1 ? 'play' : 'plays'}`;
  }
  if (kind === 'hambi') {
    if (value === null) return 'N/A (no difficulty)';
    return `${value} ${value === 1 ? 'Hambi' : 'Hambis'} (${HAMBI_LABELS[value - 1]})`;
  }
  if (value === null) return 'stars from its plays';
  return `${value} ${value === 1 ? 'star' : 'stars'} (plays set to ${playsForStars(value)})`;
}

const REMOVE_USAGE =
  'Usage: /remove <I|username> <coins|darkcoins|lightcoins|bags|goldenbags|capsules> <amount> · ' +
  '/remove <I|username> tower <name> · /remove <I|username> <item|potion> <name> [count] · ' +
  '/remove <I|username> clear';

/** `/remove` (spec 2026-10-06): /give in reverse, admin-only, never global. */
function parseRemove(rest: string[]): ParseResult {
  const [target, what, ...args] = rest;
  if (!target || !what) return { ok: false, error: REMOVE_USAGE };
  if (target === 'global') return { ok: false, error: '/remove cannot target global' };
  const ok = (value: string, amount: number): ParseResult => ({
    ok: true,
    command: { cmd: 'remove', target, what: what as RemoveWhat, value, amount },
  });
  const wholeNumber = (raw: string, max: number, label: string): number | string => {
    if (!/^[0-9]+$/.test(raw)) return `${label} needs a whole number`;
    const n = parseInt(raw, 10);
    return n < 1 || n > max ? `${label} must be 1–${max}` : n;
  };

  if (what === 'clear') return args.length === 0 ? ok('', 0) : { ok: false, error: REMOVE_USAGE };
  if (
    what === 'coins' ||
    what === 'darkcoins' ||
    what === 'lightcoins' ||
    what === 'bags' ||
    what === 'goldenbags' ||
    what === 'capsules'
  ) {
    if (args.length !== 1) return { ok: false, error: REMOVE_USAGE };
    const n = wholeNumber(args[0], REMOVE_AMOUNT_MAX, what);
    return typeof n === 'string' ? { ok: false, error: n } : ok('', n);
  }
  if (what === 'tower') {
    if (args.length !== 1) return { ok: false, error: REMOVE_USAGE };
    if (!(SHOP_ORDER as readonly string[]).includes(args[0])) {
      return { ok: false, error: `Unknown tower "${args[0]}". Towers: ${SHOP_ORDER.join(', ')}` };
    }
    return ok(args[0], 1);
  }
  if (what === 'item' || what === 'potion') {
    if (args.length < 1 || args.length > 2) return { ok: false, error: REMOVE_USAGE };
    const name = args[0].toLowerCase();
    if (what === 'item' && !isGiftItem(name)) {
      return { ok: false, error: `Unknown item "${args[0]}". Items: ${GIFT_ITEMS.join(', ')}` };
    }
    if (what === 'potion' && !potionFromCommandName(name)) {
      return {
        ok: false,
        error: `Unknown potion "${args[0]}". Potions: ${Object.keys(POTION_COMMAND_NAMES).join(', ')}`,
      };
    }
    if (args.length === 1) return ok(name, 1);
    const n = wholeNumber(args[1], REMOVE_COUNT_MAX, 'count');
    return typeof n === 'string' ? { ok: false, error: n } : ok(name, n);
  }
  return { ok: false, error: `Unknown remove kind "${what}". Kinds: ${REMOVE_KINDS.join(', ')}` };
}

/** UI-side mirror of the RPCs' role ladder; the SQL stays the enforcer. */
export function commandRefusal(role: Role, command: ParsedCommand): string | null {
  if (role === 'user') return 'Not allowed';
  if (command.cmd === 'give') {
    // A potion may go global from a higher dev too; everything else stays
    // admin-only globally. `dev_give` enforces the same rule server-side.
    if (command.target === 'global' && role !== 'admin') {
      if (!(command.what === 'potion' && role === 'higher_dev')) {
        return command.what === 'potion'
          ? 'Only higher devs and admins can give potions globally'
          : 'Only admins can give globally';
      }
    }
    if (role === 'lower_dev' && command.target !== 'I')
      return 'Lower devs can only give to themselves (target I)';
    if (command.what === 'coins' && role !== 'admin' && Number(command.value) > DEV_COIN_GIVE_MAX) {
      return `Only admins can give more than ${DEV_COIN_GIVE_MAX} coins`;
    }
    // The two BULK achievement operations are admin-only. Granting every
    // achievement at once, or wiping the set, is not the same kind of act as
    // handing over one id: `all` skips the whole earning ladder and `reset`
    // destroys a player's record. Single gives stay open to every elevated
    // role. `dev_give` enforces the same rule server-side — this is the UI
    // mirror, not the gate.
    const bulkAchieve =
      command.what === 'achieve' && (command.value === 'all' || command.value === 'reset');
    if (bulkAchieve && role !== 'admin') {
      return `Only admins can use /give achieve ${command.value}`;
    }
    return null;
  }
  if (command.cmd === 'ban') {
    if (role === 'lower_dev') return 'Lower devs cannot ban';
    return null;
  }
  if (command.cmd === 'op') {
    if (role !== 'admin') return 'Only admins can use /op';
    return null;
  }
  if (command.cmd === 'restock') {
    if (role === 'lower_dev') return 'Only higher devs and admins can restock the Potion Shop';
    return null;
  }
  if (command.cmd === 'skip') {
    if (role === 'lower_dev') return 'Only higher devs and admins can use /skip';
    return null;
  }
  if (command.cmd === 'remove') {
    if (role !== 'admin') return 'Only admins can use /remove';
    return null;
  }
  // Explore (spec 2026-11-02): admins rate and ban; every dev may suggest.
  if (command.cmd === 'rate') {
    if (role !== 'admin') return 'Only admins can rate levels — try /suggest';
    return null;
  }
  if (command.cmd === 'server') {
    if (role !== 'admin') return 'Only admins can ban levels — try /suggest';
    return null;
  }
  if (command.cmd === 'suggest') return null;
  // message: ADMINS ONLY since 2026-10-24 (higher devs could since round 3).
  // dev_message enforces the same rule server-side.
  if (role !== 'admin') return 'Only admins can send messages';
  return null;
}

export interface CommandHelp {
  usage: string;
  description: string;
  example: string;
  visibleTo: (role: Role) => boolean;
}

export const COMMAND_HELP: readonly CommandHelp[] = [
  {
    usage:
      '/give <I|username|global> <coins|tower|unlock|level|bags|rank|achieve|hublevel|potion|debris|moonstone|capycoins|capsules|goldenbags|darkcoins|lightcoins|icon|item> <value>',
    description:
      'Give coins, a tower, a feature unlock (shop2, evolve, hub, potionshop), level progress, ' +
      'hub level progress (hublevel N marks The Hub levels 1..N won), takeout bags, Space Debris, the Moon Stone (moonstone 1), or a potion ' +
      '(pickle, chili, cola, bottleburger, samurai, oliveoil — lands in Storage; potions may go global from higher devs too) — free. ' +
      'item gives one Storage item — diamond, varietyorb, moonstone, debris, goldstone, ' +
      'capsule, goldenbag, capystone, darkshard, evilorb, lightshard, lightorb, darkchest, lightchest, shroom, snowshroom or tokenofshroom — and always puts a fresh one on the shelf, even if theirs was used. ' +
      'icon gives an Avatar Shop icon by code (patty4, generic, ninja4, crown, shrekbook2, ' +
      'upsidedown, takeout, wiseness) — it appears starred in EDIT AVATAR. ' +
      "rank sets a player's rank (1–40); achieve awards an achievement by id " +
      "(admins can also pass 'all' or 'reset' to grant every achievement or clear them). " +
      'Target I is yourself; global reaches every player online right now (admins only). ' +
      'Coins: up to 1000000000 from an admin, 1000000 from a dev. ' +
      'Lower devs can only give to themselves.',
    example: '/give user123 coins 40',
    visibleTo: (role) => role !== 'user',
  },
  {
    usage: '/message <username|global> "text"',
    description:
      "Show a banner at the top of the player's screen with your username as the sender. " +
      'global reaches every player online right now. Admins only.',
    example: '/message Df34 "Enjoy"',
    visibleTo: (role) => role === 'admin',
  },
  {
    usage: '/ban <username>',
    description:
      'Ban a regular user: they are signed out, cannot sign back in, and vanish ' +
      'from user search. Signing up again with the same email starts them fresh. ' +
      'Devs and admins cannot be banned.',
    example: '/ban user123',
    visibleTo: (role) => role === 'higher_dev' || role === 'admin',
  },
  {
    usage: '/restock potionshop',
    description:
      "Restock every player's Potion Shop right now and restart its ten-minute timer " +
      '(higher devs and admins).',
    example: '/restock potionshop',
    visibleTo: (role) => role === 'higher_dev' || role === 'admin',
  },
  {
    usage: '/op <I|username>',
    description:
      'Give a player everything: every tower, all episodes unlocked, Evolve and ' +
      'all shop pages, 100000 coins, and every evolvable tower evolved.',
    example: '/op user123',
    visibleTo: (role) => role === 'admin',
  },
  {
    usage: '/skip <I|username>',
    description:
      'Skip the wait: every takeout bag (Normal and Golden) and Capy capsule still ' +
      'cooking is ready right now, and a level being played jumps straight to its ' +
      'Level Complete screen with the coins and prizes a win pays (higher devs and admins).',
    example: '/skip I',
    visibleTo: (role) => role === 'higher_dev' || role === 'admin',
  },
  {
    usage:
      '/remove <I|username> <coins|darkcoins|lightcoins|bags|goldenbags|capsules|tower|item|potion|clear> [name] [amount]',
    description:
      '/give in reverse: take coins, Dark or Light Hambi Coins, takeout bags (bags, goldenbags), capsules, ' +
      'a tower by name, or Storage items and potions by name with an optional count (default 1). ' +
      'Amounts never go below zero, and naming a tower or item the player does not have does ' +
      'nothing. Towers the campaign hands out by episode cannot be removed. ' +
      'clear empties their Storage: every item, potion and bag on the shelf. ' +
      'Admins only; no global.',
    example: '/remove I potion chili 3',
    visibleTo: (role) => role === 'admin',
  },
  {
    usage: '/rate <hambi|star|plays> <code> <number>',
    description:
      'Rate a published Explore level or chapter by its code (#K7Q2 on its row). hambi sets its ' +
      'difficulty: 1 Easy, 2 Medium, 3 Mild, 4 Hard, 5 Extreme (none clears it back to N/A). ' +
      'star 0–5 sets its plays to that star (0, 10, 25, 50, 100 or 150) so its stars follow the ' +
      'plays from there (auto just unpins an old rating). plays sets the play count, ' +
      '0–1,000,000. Admins only.',
    example: '/rate hambi K7Q2 3',
    visibleTo: (role) => role === 'admin',
  },
  {
    usage: '/server <ban|unban> <code>',
    description:
      'ban takes a published level off Explore; its maker still has it but cannot publish it ' +
      'again until an admin uses unban. unban works on banned levels only: a pending unban ' +
      "request's version goes live, otherwise the last approved version comes back. Admins only.",
    example: '/server ban K7Q2',
    visibleTo: (role) => role === 'admin',
  },
  {
    usage: '/suggest <code> <number> [stars|hambis] · /suggest <code> <ban|unban>',
    description:
      'Suggest a rating (stars by default, or hambis for a difficulty) or a ban/unban for a level. ' +
      "It lands in the admins' inbox; nothing else changes.",
    example: '/suggest K7Q2 4 stars',
    visibleTo: (role) => role !== 'user',
  },
];
